import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  firstValueFrom,
  lastValueFrom,
  Observable,
  tap,
  throwError,
} from 'rxjs';
import { HttpService } from './http.service';
import {
  IClient,
  IDefaultConfig,
  IProject,
} from '../common/models/home-user.interface';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { DefaultParamSource } from '../common/enums/params.enum';
import { ClientsService } from './clients.service';
import { ProjectService } from './project.service';
import { ThemeService } from './theme.service';
import { AlertService } from './alert.service';
import { ITheme } from '../common/models/theme.interface';
import { Theme } from '../common/enums/theme.enum';
import { BusyService } from './busy.service';

@Injectable({
  providedIn: 'root',
})
export class SessionContextService {
  private clientChanged$ = new BehaviorSubject<IClient | null>(
    this.getStoredDefaultClient() || null,
  );

  private projectChange$ = new BehaviorSubject<IProject | null>(
    this.getStoredDefaultProject() || null,
  );

  private client: IClient | null = null;
  private project: IProject | null = null;

  constructor(
    private httpService: HttpService,
    private route: ActivatedRoute,
    private clientService: ClientsService,
    private projectService: ProjectService,
    private themeService: ThemeService,
    private alertService: AlertService,
    private busyService: BusyService,
    private router: Router,
  ) {}

  getDefaultData(): Observable<IDefaultConfig> {
    return this.httpService.get(`${environment.API_URL}/default-config`).pipe(
      tap((response: IDefaultConfig) => {
        const client = response.data.client;
        const project = response.data.project;

        sessionStorage.setItem('idProject', project.id);
        sessionStorage.setItem('idClient', client.id);

        sessionStorage.setItem('client', JSON.stringify(client));
        sessionStorage.setItem('project', JSON.stringify(project));

        this.clientChanged$.next(client);
        this.projectChange$.next(project);
      }),
      catchError((error) => {
        console.error('❌ Error al obtener los datos por default', error);

        return throwError(() => {
          console.error(error);
          // this.router.navigate(['invalid-url']);
        });
      }),
    );
  }

  saveParamsToSessionStorage(): void {
    this.route.queryParamMap.subscribe((params) => {
      const idProject = params.get('idProject');
      const idClient = params.get('idClient');

      if (idProject && idClient) {
        sessionStorage.setItem('idProject', idProject);
        sessionStorage.setItem('idClient', idClient);
      }
    });
  }

  getStoredDefaultClient(): IClient | null {
    const clientString = sessionStorage.getItem('client');
    return clientString ? (JSON.parse(clientString) as IClient) : null;
  }

  getStoredDefaultProject(): IProject | null {
    const projectString = sessionStorage.getItem('project');
    return projectString ? (JSON.parse(projectString) as IProject) : null;
  }

  hasStoredClientAndProject(): boolean {
    const client = this.getStoredDefaultClient();
    const project = this.getStoredDefaultProject();
    return !!client && !!project;
  }

  hasUrlParams(): boolean {
    const idProject = this.route.snapshot.queryParamMap.get('idProject');
    const idClient = this.route.snapshot.queryParamMap.get('idClient');

    const isValid =
      idProject !== null &&
      idClient !== null &&
      !isNaN(Number(idProject)) &&
      !isNaN(Number(idClient));

    return isValid;
  }

  getStoredParams(): { projectId: string | null; clientId: string | null } {
    const rawProjectId = sessionStorage.getItem('idProject');
    const rawClientId = sessionStorage.getItem('idClient');

    const projectId =
      rawProjectId !== null && !isNaN(Number(rawProjectId))
        ? rawProjectId
        : null;

    const clientId =
      rawClientId !== null && !isNaN(Number(rawClientId)) ? rawClientId : null;

    return {
      projectId,
      clientId,
    };
  }

  async initDefaultParams(): Promise<DefaultParamSource> {
    const hasUrlParams = this.hasUrlParams();

    if (hasUrlParams) {
      sessionStorage.clear();
      // Si hay parámetros en la URL, los guardo en sessionStorage
      this.saveParamsToSessionStorage();
      return DefaultParamSource.URL;
    }

    const storedParams = this.getStoredParams();

    if (storedParams.projectId && storedParams.clientId) {
      // Si no hay parámetros en la URL pero sí en sessionStorage, uso esos
      return DefaultParamSource.SESSION_STORAGE;
    }

    // Si no hay parámetros en la URL ni en sessionStorage, llamo a la API
    await lastValueFrom(this.getDefaultData());
    return DefaultParamSource.API;
  }

  async initializeContext(): Promise<void> {
    this.busyService.busy();
    try {
      const source = await this.initDefaultParams();

      switch (source) {
        case DefaultParamSource.URL:
          // lógica si vino desde URL o si tengo los datos en sessionStorage
          const storedParams = this.getStoredParams();
          const idClient = storedParams.clientId as string;
          const idProject = storedParams.projectId as string;
          const hasStoredClientAndProject = this.hasStoredClientAndProject();
          if (!hasStoredClientAndProject) {
            this.loadClientAndProjectData(idClient, idProject);
          } else {
            this.project = this.getStoredDefaultProject();
            this.client = this.getStoredDefaultClient();
            if (this.client) {
              this.setTheme(this.client);
            }
          }
          break;
        case DefaultParamSource.SESSION_STORAGE:
          // lógica si se obtuvo desde sessionStorage
          this.project = this.getStoredDefaultProject();
          this.client = this.getStoredDefaultClient();
          if (!this.project || !this.client) {
            this.alertService.setErrorMessage(
              'Error, please enter a valid url',
            );
            this.router.navigate(['invalid-url']);
            break;
          }
          if (this.client) {
            this.setTheme(this.client);
          }
          break;
        case DefaultParamSource.API:
          // lógica si se obtuvo desde la API
          // Los datos ya están disponibles, projectDefault y clientDefault
          this.project = this.getStoredDefaultProject();
          this.client = this.getStoredDefaultClient();

          if (this.client) {
            this.setTheme(this.client);
          }
          break;
      }
    } catch (error) {
      console.error('Error inicializando el contexto:', error);
    } finally {
      this.busyService.idle();
    }
  }

  setTheme(client: IClient) {
    const colors: ITheme = {
      type: client.color_type as Theme,
      primaryColor: client.color_primary,
      secondaryColor: client.color_secondary,
      tertiaryColor: client.color_tertiary,
      textColor: client.color_text,
      formBg: 'white',
    };
    this.themeService.setThemeColors(colors);
    if (colors.type === Theme.LIGHT) {
      this.themeService.applyLightTheme();
    } else {
      this.themeService.applyDarkTheme();
    }
  }

  async loadClientAndProjectData(
    idClient: string,
    idProject: string,
  ): Promise<void> {
    try {
      await Promise.all([
        firstValueFrom(
          this.clientService.getClientById(idClient).pipe(
            tap((response) => {
              if (response.data) {
                this.client = response.data;
                this.setTheme(this.client as IClient);
                sessionStorage.setItem('client', JSON.stringify(response.data));
                this.clientChanged$.next(response.data);
              }
            }),
          ),
        ),
        firstValueFrom(
          this.projectService.getProjectById(idProject).pipe(
            tap((response) => {
              if (response.data) {
                this.project = response.data;
                sessionStorage.setItem(
                  'project',
                  JSON.stringify(response.data),
                );
                this.projectChange$.next(response.data);
              }
            }),
          ),
        ),
      ]);
    } catch (error) {
      console.error('Loading client and project data failed:', error);
      this.router.navigate(['invalid-url']);
    }
  }

  getClientSessionObservable(): Observable<IClient | null> {
    return this.clientChanged$.asObservable();
  }

  getProjectSessionObservable(): Observable<IProject | null> {
    return this.projectChange$.asObservable();
  }
}
